import { useMutation, useQueryClient } from '@tanstack/react-query';
import sessionsService from '@/lib/api/sessions';
import type { SalesAnalyticsResponse, SalesSummary, SessionsResponse } from '@/lib/api/sessions';

type QualificationStatusValue = SalesSummary['qualification_status'];

type UpdateQualificationStatusInput = {
  sessionId: string;
  qualification_status: QualificationStatusValue;
};

const updateSessionsResponse = (
  data: SessionsResponse | undefined,
  sessionId: string,
  qualification_status: QualificationStatusValue
) => {
  if (!data?.sessions) return data;
  return {
    ...data,
    sessions: data.sessions.map((session) =>
      session.session_recording_id === sessionId
        ? { ...session, updated_qualification_status: qualification_status }
        : session
    ),
  };
};

const updateAnalyticsResponse = (
  data: SalesAnalyticsResponse | undefined,
  qualification_status: QualificationStatusValue
) => {
  if (!data?.sales_summary) return data;
  return {
    ...data,
    sales_summary: {
      ...data.sales_summary,
      updated_qualification_status: qualification_status,
    },
  };
};

export const useUpdateSessionQualificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, qualification_status }: UpdateQualificationStatusInput) => {
      const response = await sessionsService.updateSessionQualificationStatus(
        sessionId,
        qualification_status
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update qualification status');
      }
      return response.data;
    },
    onMutate: async ({ sessionId, qualification_status }) => {
      await queryClient.cancelQueries({ queryKey: ['organizationSessions'] });
      await queryClient.cancelQueries({ queryKey: ['salesAnalytics', sessionId] });

      const previousSessions = queryClient.getQueriesData<SessionsResponse>({
        queryKey: ['organizationSessions'],
      });
      const previousAnalytics = queryClient.getQueryData<SalesAnalyticsResponse>([
        'salesAnalytics',
        sessionId,
      ]);

      queryClient.setQueriesData<SessionsResponse>(
        { queryKey: ['organizationSessions'] },
        (data) => updateSessionsResponse(data, sessionId, qualification_status)
      );
      queryClient.setQueryData<SalesAnalyticsResponse>(
        ['salesAnalytics', sessionId],
        (data) => updateAnalyticsResponse(data, qualification_status)
      );

      return { previousSessions, previousAnalytics };
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      for (const [key, data] of context.previousSessions) {
        queryClient.setQueryData(key, data);
      }
      queryClient.setQueryData(['salesAnalytics', variables.sessionId], context.previousAnalytics);
    },
    onSuccess: (data, variables) => {
      const normalizedStatus = data.updated_qualification_status || variables.qualification_status;
      queryClient.setQueriesData<SessionsResponse>(
        { queryKey: ['organizationSessions'] },
        (sessions) => updateSessionsResponse(sessions, variables.sessionId, normalizedStatus)
      );
      queryClient.setQueryData<SalesAnalyticsResponse>(
        ['salesAnalytics', variables.sessionId],
        (analytics) => updateAnalyticsResponse(analytics, normalizedStatus)
      );
    },
  });
};

export default useUpdateSessionQualificationStatus;
